import { baseApi } from '../api/baseApi';
import type {
    IntelSearchRequest,
    IntelSearchResponse,
    IntelSaveRequest,
    IntelSaveResponse,
    MapDataResponse,
} from '../api/types';

// Interface for the map filter request payload
export interface MapFilterRequest {
    fetch_id: string;
    country_code: string;
    page?: number;
    page_size?: number;
}

// Extend the base API with search-specific endpoints
export const searchApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // POST /intel/search/ - Search threat intelligence from multiple providers
        searchThreatIntel: builder.mutation<IntelSearchResponse, IntelSearchRequest>({
            query: (data) => ({
                url: '/intel/search/',
                method: 'POST',
                body: data,
            }),
            // We don't cache search results as they're temporary and expire
            // Each page request creates a new API call
            invalidatesTags: [{ type: 'Search', id: 'LIST' }],
        }),

        // POST /intel/save/ - Save selected threat intelligence results to database
        saveSearchResults: builder.mutation<IntelSaveResponse, IntelSaveRequest>({
            query: (data) => ({
                url: '/intel/save/',
                method: 'POST',
                body: data,
            }),
            // After saving, invalidate incidents list to show new saved items
            invalidatesTags: [
                { type: 'Incident', id: 'LIST' },
                { type: 'Search', id: 'LIST' },
            ],
        }),

        // GET /intel/map-data/ - Get geolocation data for threat intelligence map visualization
        getMapData: builder.query<MapDataResponse, string>({
            query: (fetchId) => ({
                url: '/intel/map-data/',
                method: 'GET',
                params: { fetch_id: fetchId },
            }),
            // Map data is tied to search results, so we cache it temporarily
            // All pages from the same search share the same map data
            providesTags: (_result, _error, fetchId) => [
                { type: 'Search', id: `MAP_${fetchId}` }
            ],
        }),

        // GET /intel/map-filter/ - Get filtered map data by country
        getMapFilter: builder.query<any, MapFilterRequest>({
            query: ({ fetch_id, country_code, page = 1, page_size = 500 }) => ({
                url: '/intel/map-filter/',
                method: 'GET',
                params: { 
                    fetch_id, 
                    country_code, 
                    page, 
                    page_size 
                },
            }),
            // Cache the filter results based on fetchId and country code
            providesTags: (_result, _error, arg) => [
                { type: 'Search', id: `MAP_FILTER_${arg.fetch_id}_${arg.country_code}` }
            ],
        }),
    }),
    overrideExisting: false,
});

// Export hooks for usage in components
export const {
    useSearchThreatIntelMutation,
    useSaveSearchResultsMutation,
    useGetMapDataQuery,
    useLazyGetMapDataQuery,
    useGetMapFilterQuery,
    useLazyGetMapFilterQuery,
} = searchApi;